// ============================================================
// ModalFrame.tsx — the SHARED modal frame (case-entry-modals plan, step 1).
//
// Distilled from the redesigned modals so every modal shares one frame:
//   • overlay + centering   CaseQuickViewModal.tsx:364-366 (same in
//                           AppointmentFormModal.tsx:372-376, TasksView.tsx:1537-1540)
//   • hero band (header)    CaseQuickViewModal.tsx:315-321 — forest→brand
//                           gradient, white-alpha pill(s), ✕, 22px title,
//                           13.5px subtitle
//   • footer band           CaseQuickViewModal.tsx:464/542 — border-t +
//                           bg-casevo-input, px-6 sm:px-8 py-4, gap-2.5
//   • body padding          design-language §4 "modal body px-6 sm:px-8 py-6"
// and the approved mockup (casevo-case-entry-modals.html): the header gains an
// optional SLOT under the title (search box / step indicator), the footer is
// LEFT (Back / Cancel / status) + RIGHT (exactly one green primary), and the
// only per-modal variables are WIDTH, the header slot and the footer content.
//
// Adds the behaviour the old frames lacked (shared, so every adopter gets it):
// role="dialog" + aria-modal + aria-labelledby/-describedby, Esc to close
// (top-most frame only when stacked), backdrop click to close (press AND
// release on the backdrop, so a text-selection drag out of a field never
// closes), a Tab focus-trap, initial focus + focus restore, and a
// `dismissible={false}` lock (e.g. Import while rows are being created).
//
// THEME: renders INLINE by default — exactly like today's modals — so a modal
// opened inside a theme scope (the calendar's `dark` / `casevo-light` wrapper,
// TasksView.tsx:1536) keeps it. `portal` moves it to <body> (escapes
// overflow/stacking contexts); pair it with `themeClassName` to carry a scope.
//
// Adopters: NewCaseModal, ImportCasesModal, LinkCasePickerModal, DialogueBox,
// ConfirmDialogue (bare mode — no hero band).
// ============================================================
import React, { useCallback, useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'

export type ModalFrameSize = 'sm' | 'md' | 'lg' | 'xl' | 'xxl'

// sm = confirm dialogs (design-language §3.4 "down to max-w-md")
// md = New case (mockup .w-md, 640px → max-w-2xl 672px)
// lg = quick-view / task / Open existing / Import (max-w-3xl)
// xl = appointment (AppointmentFormModal.tsx:376 max-w-4xl)
const SIZE_CLASS: Record<ModalFrameSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-2xl',
  lg: 'max-w-3xl',
  xl: 'max-w-4xl',
  // Invoicing Stage 1 — the raise-a-bill modal (mockup .modal max-width 1180px).
  xxl: 'max-w-[1180px]',
}

// White-alpha pill on the hero band — CaseQuickViewModal.tsx:319 / :322.
export const HERO_PILL =
  'inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-md bg-white/20'
export const HERO_PILL_SOFT =
  'inline-flex items-center text-[11px] font-semibold px-2.5 py-1 rounded-md bg-white/10 font-mono'

export interface ModalFrameProps {
  /** Default true. When false nothing renders (lets callers keep the frame mounted). */
  isOpen?: boolean
  onClose: () => void

  // ── header (hero band) ──
  /** Eyebrow pill text, e.g. "New case" / "Import". Rendered in HERO_PILL. */
  eyebrow?: React.ReactNode
  /** Icon inside the eyebrow pill (w-3.5 h-3.5 line icon). */
  eyebrowIcon?: React.ReactNode
  /** Extra pills after the eyebrow (e.g. a REF / file-name pill — use HERO_PILL_SOFT). */
  pills?: React.ReactNode
  title: React.ReactNode
  subtitle?: React.ReactNode
  /** Optional slot UNDER the title — Open existing's search, Import's step indicator. */
  headerSlot?: React.ReactNode

  // ── body ──
  children?: React.ReactNode
  /** Default true (px-6 sm:px-8 py-6). False → edge-to-edge (e.g. a full-bleed list). */
  bodyPadding?: boolean
  bodyClassName?: string

  // ── footer (shaded band) ──
  /** Left side: Back / Cancel / a status line. */
  footerLeft?: React.ReactNode
  /** Right side: the ONE green primary (+ an optional secondary before it). */
  footerRight?: React.ReactNode
  /** Full override of the footer's inner content (replaces left/right). */
  footer?: React.ReactNode

  // ── frame ──
  size?: ModalFrameSize
  /** Default true. False blocks Esc, backdrop-click and the ✕ (e.g. mid-import). */
  dismissible?: boolean
  /** Default true. */
  closeOnBackdrop?: boolean
  /** Focused on open; else the first focusable in the BODY; else the dialog. */
  initialFocusRef?: React.RefObject<HTMLElement | null>
  /** Accessible name when `title` isn't plain text. */
  ariaLabel?: string
  /** Render into <body> via a portal. Default false (inline, inherits theme scope). */
  portal?: boolean
  /** Theme scope applied on the overlay ('dark' | 'casevo-light'), mainly with `portal`. */
  themeClassName?: string
  /** Overlay z-index class. Default z-[70] (all current modals). */
  zIndexClassName?: string
  /** Extra classes on the white shell (e.g. AppointmentFormModal's dark:bg-[#18241d]). */
  className?: string
  /** SCROLL-BODY mode: the shell is capped to the viewport (minus the
   *  overlay's p-4) and becomes a flex column — the hero band and footer stay
   *  PINNED and only the body scrolls. For long, action-heavy modals whose
   *  footer must stay in view (ComposeEmailModal: timer + Send). Ignored in
   *  bare mode. Default false (the overlay scrolls the whole sheet, as before). */
  scrollBody?: boolean
  /** BARE mode: keep the shared overlay / Esc (top-most) / backdrop / focus
   *  trap + restore / stacking, but render NO hero band, body or footer —
   *  `children` IS the dialog's whole content and `className` styles the
   *  shell (width, radius, surface, shadow). Used by the light ConfirmDialogue.
   *  Pass `ariaLabel` (the title isn't rendered by the frame). */
  bare?: boolean
}

// Esc must only close the TOP-most frame when frames stack (e.g. a confirm
// over a modal). Module-level stack of open frame ids, newest last.
const openStack: string[] = []

// A non-ModalFrame modal (role=dialog aria-modal) that sits ABOVE `dialog`:
// rendered inside it, or after it in document order. ModalFrames among
// themselves are ordered by openStack instead (marked data-modal-frame).
const otherModalAbove = (dialog: HTMLElement): boolean =>
  Array.from(document.querySelectorAll<HTMLElement>('[aria-modal="true"]')).some(
    (el) =>
      el !== dialog &&
      !el.hasAttribute('data-modal-frame') &&
      (dialog.contains(el) || (dialog.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0),
  )

// (batch 2) iframes count: a document preview (DocumentPreviewFrame) is a tab
// stop, so Tab / Shift+Tab cycle through it like any other control.
export const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe:not([tabindex="-1"]), [contenteditable="true"], [tabindex]:not([tabindex="-1"])'
// Rendered and not visibility:hidden (a preview's hidden, still-loading layer).
export const isTabStop = (el: HTMLElement): boolean =>
  (el.offsetParent !== null || el === document.activeElement) && getComputedStyle(el).visibility !== 'hidden'

const ModalFrame: React.FC<ModalFrameProps> = ({
  isOpen = true,
  onClose,
  eyebrow,
  eyebrowIcon,
  pills,
  title,
  subtitle,
  headerSlot,
  children,
  bodyPadding = true,
  bodyClassName = '',
  footerLeft,
  footerRight,
  footer,
  size = 'lg',
  dismissible = true,
  closeOnBackdrop = true,
  initialFocusRef,
  ariaLabel,
  portal = false,
  themeClassName = '',
  zIndexClassName = 'z-[70]',
  className = '',
  bare = false,
  scrollBody = false,
}) => {
  const uid = useId()
  const titleId = `${uid}-title`
  const subtitleId = `${uid}-subtitle`
  const dialogRef = useRef<HTMLDivElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const pressStartedOnBackdrop = useRef(false)

  // Latest props for the stable listeners (no re-register per render).
  const latest = useRef({ onClose, dismissible })
  latest.current = { onClose, dismissible }

  const requestClose = useCallback(() => {
    if (latest.current.dismissible) latest.current.onClose()
  }, [])

  // Stack registration + initial focus + focus restore.
  useEffect(() => {
    if (!isOpen) return
    openStack.push(uid)
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const raf = requestAnimationFrame(() => {
      // Respect a child's own autoFocus (e.g. TimerLeaveDialog's Save button,
      // the rename input) — only move focus if it isn't already inside us.
      const active = document.activeElement
      if (!initialFocusRef?.current && active && dialogRef.current?.contains(active) && active !== dialogRef.current) return
      const target =
        initialFocusRef?.current ??
        bodyRef.current?.querySelector<HTMLElement>(FOCUSABLE) ??
        dialogRef.current
      target?.focus()
    })
    return () => {
      cancelAnimationFrame(raf)
      const i = openStack.lastIndexOf(uid)
      if (i !== -1) openStack.splice(i, 1)
      // Restore focus to the trigger (if it's still in the document).
      if (previouslyFocused && document.contains(previouslyFocused)) previouslyFocused.focus()
    }
    // initialFocusRef is a ref object — stable; only re-run on open/close.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, uid])

  // Esc (top-most only) + Tab focus-trap.
  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent): void => {
      if (e.defaultPrevented) return
      // Top-most ModalFrame only …
      if (openStack[openStack.length - 1] !== uid) return
      const dialog = dialogRef.current
      if (!dialog) return
      // … and only while focus is in THIS frame (or nowhere). A non-ModalFrame
      // overlay stacked on top (e.g. ConfirmDialogue / ClientPickerModal, which
      // handle their own Esc on window) owns the keys:
      //   • focus inside ANOTHER aria-modal (stacked, or nested in our body) → not ours;
      //   • focus on <body> (e.g. after clicking that overlay's whitespace) while
      //     another non-frame aria-modal sits ABOVE us (later in the DOM, or
      //     inside our dialog) → not ours either. Previously Esc here closed BOTH
      //     (the overlay via its own handler + this frame), and Tab pulled focus
      //     into this frame behind the overlay.
      const active = document.activeElement
      const activeModal = active instanceof Element ? active.closest('[aria-modal="true"]') : null
      if (activeModal && activeModal !== dialog) return
      const onBody = !active || active === document.body
      if (!onBody && !dialog.contains(active)) return
      if (onBody && otherModalAbove(dialog)) return
      if (e.key === 'Escape') {
        e.preventDefault()
        // Stop it reaching WINDOW-level Esc listeners of a host overlay
        // underneath (e.g. AdminPage's member modal, CalendarDayPanel) —
        // otherwise one Esc closed both this frame and its host.
        e.stopPropagation()
        requestClose()
        return
      }
      if (e.key !== 'Tab') return
      const focusables = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(isTabStop)
      if (focusables.length === 0) {
        e.preventDefault()
        dialog.focus()
        return
      }
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      const inside = active instanceof Node && dialog.contains(active)
      if (e.shiftKey && (active === first || !inside || active === dialog)) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && (active === last || !inside)) {
        e.preventDefault()
        first.focus()
      }
    }
    // Tab pressed INSIDE an iframe never reaches this document's keydown, so
    // the browser can move focus straight out of the dialog. If focus leaves
    // this (top-most) frame from an iframe in it, wrap it like Tab would:
    // past the end → the first control; before the start → the last.
    let lastFocused: Element | null = null
    const onFocusIn = (e: FocusEvent): void => {
      const dialog = dialogRef.current
      const target = e.target
      const from = lastFocused
      lastFocused = target instanceof Element ? target : null
      if (!dialog || !(target instanceof Node) || dialog.contains(target)) return
      if (openStack[openStack.length - 1] !== uid) return
      if (!(from instanceof HTMLIFrameElement) || !dialog.contains(from)) return
      const focusables = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(isTabStop)
      if (!focusables.length) return
      const after = (dialog.compareDocumentPosition(target) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0
      ;(after ? focusables[0] : focusables[focusables.length - 1]).focus()
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('focusin', onFocusIn)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('focusin', onFocusIn)
    }
  }, [isOpen, uid, requestClose])

  if (!isOpen) return null

  // Backdrop click: the overlay AND the centering wrapper are both "backdrop"
  // (the wrapper fills the overlay around the shell). One handler on the
  // overlay checks the real target, and closes only when the press both
  // STARTED and ENDED on the backdrop (a drag out of a field never closes).
  const isBackdrop = (target: EventTarget): boolean =>
    target === overlayRef.current || target === wrapperRef.current
  const onBackdropDown = (e: React.MouseEvent): void => {
    pressStartedOnBackdrop.current = isBackdrop(e.target)
  }
  const onBackdropUp = (e: React.MouseEvent): void => {
    if (closeOnBackdrop && pressStartedOnBackdrop.current && isBackdrop(e.target)) requestClose()
    pressStartedOnBackdrop.current = false
  }

  const hasFooter = footer !== undefined || footerLeft !== undefined || footerRight !== undefined

  const frame = (
    <div className={themeClassName || undefined}>
      {/* Overlay — scrolls; centers via min-h-full (design-language §3.4). */}
      <div
        ref={overlayRef}
        className={`fixed inset-0 ${zIndexClassName} overflow-y-auto bg-black/40`}
        onMouseDown={onBackdropDown}
        onMouseUp={onBackdropUp}
      >
        <div ref={wrapperRef} className="flex min-h-full items-center justify-center p-4">
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            data-modal-frame=""
            aria-labelledby={ariaLabel ? undefined : titleId}
            aria-label={ariaLabel}
            aria-describedby={subtitle ? subtitleId : undefined}
            tabIndex={-1}
            className={bare
              ? `w-full focus:outline-none ${className}`
              : `w-full ${SIZE_CLASS[size]} rounded-2xl overflow-hidden bg-casevo-surface shadow-2xl text-casevo-forest focus:outline-none ${scrollBody ? 'max-h-[calc(100vh-2rem)] flex flex-col' : ''} ${className}`}
          >
            {bare ? (
              <div ref={bodyRef}>{children}</div>
            ) : (
            <>
            {/* ── Hero band — CaseQuickViewModal.tsx:315-321 ── */}
            <div className="relative shrink-0 px-6 sm:px-8 py-6 text-white bg-gradient-to-br from-casevo-forest to-casevo-brand dark:from-[#1a4230] dark:to-[#2d6a4f]">
              <button
                type="button"
                onClick={requestClose}
                disabled={!dismissible}
                aria-label="Close"
                className="absolute top-4 right-4 w-8 h-8 rounded-lg bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-colors focus:outline-none casevo-focus disabled:opacity-40 disabled:cursor-not-allowed"
              >
                ✕
              </button>
              {(eyebrow !== undefined || pills !== undefined) && (
                <div className="flex items-center gap-2 mb-3 flex-wrap pr-12">
                  {eyebrow !== undefined && (
                    <span className={HERO_PILL}>
                      {eyebrowIcon}
                      {eyebrow}
                    </span>
                  )}
                  {pills}
                </div>
              )}
              <h2 id={titleId} className={`text-[22px] font-bold leading-tight mb-1 ${eyebrow === undefined && pills === undefined ? 'pr-12' : ''}`}>
                {title}
              </h2>
              {subtitle !== undefined && (
                <div id={subtitleId} className="text-[13.5px] text-white/85">
                  {subtitle}
                </div>
              )}
              {headerSlot !== undefined && <div className="mt-4">{headerSlot}</div>}
            </div>

            {/* ── Body ── */}
            <div ref={bodyRef} className={`${bodyPadding ? 'px-6 sm:px-8 py-6' : ''} ${scrollBody ? 'flex-1 min-h-0 overflow-y-auto overscroll-contain' : ''} ${bodyClassName}`}>
              {children}
            </div>

            {/* ── Footer band — CaseQuickViewModal.tsx:464 / :542 ── */}
            {hasFooter && (
              <div className="shrink-0 flex items-center gap-2.5 px-6 sm:px-8 py-4 border-t border-casevo-border bg-casevo-input">
                {footer !== undefined ? (
                  footer
                ) : (
                  <>
                    <div className="flex items-center gap-2.5 min-w-0">{footerLeft}</div>
                    <div className="ml-auto flex flex-wrap justify-end items-center gap-2.5 min-w-0">{footerRight}</div>
                  </>
                )}
              </div>
            )}
            </>
            )}
          </div>
        </div>
      </div>
    </div>
  )

  return portal && typeof document !== 'undefined' ? createPortal(frame, document.body) : frame
}

export default ModalFrame
